import { Button, Checkbox, Col, Radio, Row, Select, Space, Modal } from 'antd';
import { any, array, func, number, object, string } from 'prop-types';
import { useContext, useEffect, useState, useRef } from 'react';

import { ChevronLeftSVG } from 'components/iconsSVG/chevronLeft';
import { ChevronSVG } from 'components/iconsSVG/chevron';
import { i18nContext, useI18n } from 'context/i18n';
import { userContext } from 'context/user';

import { dataLayerHandleEvent } from 'utils/dataLayers';
import { parseBagItemsEcommerceGA4 } from 'utils/gtmUtils';
import { getMarket, roundAndFormatPrice, nnormalSizesMap, nnormalSizeMapCountries } from 'utils/helpers';
import { defaultLocale, getCountryFromMarket } from 'utils/translations';

import { ContactBlock } from 'components/faqs';
import HomePickUp from 'components/myAccount/returnItems/homePickUp';
import PickUpInStoreModal from 'components/pickupInStoreModal';

import { CORREOS, ROYAL_MAIL_URL } from 'utils/constants/shippings';
import { TARGET_SIZE_LABEL_MARKETS } from 'utils/constants/sizes';
import { FAMILIES } from 'utils/constants/products';
import { COUNTRIES_WITH_TAXES } from 'utils/constants/payments';
import { IS_NNORMAL } from 'utils/constants/system';
import { ORDER_DETAILS_ACTIONS } from 'utils/constants/orders';

import { getOrderInfo, rejectOrder1, rejectOrder2, getDroppointsInfo } from 'actions/products';

import { LabelButtons } from '../orderComponents';
import styles from './style.module.css';
import { getDomainValue } from 'utils/domain';

const { Option } = Select;

export const SummaryReturnCosts = ({ subtotal, returnCost = null, currency = '€', taxes }) => {
  const { t, locale } = useContext(i18nContext);
  const market = getMarket(locale);

  let priceForTotal = subtotal;

  if (returnCost !== null) {
    priceForTotal = subtotal + returnCost.price;
  }
  const subtotalFormated = roundAndFormatPrice(subtotal, market, currency);
  const totalFormatted =
    taxes ? roundAndFormatPrice(taxes.taxTotalPrice + returnCost.price, market, currency) : roundAndFormatPrice(priceForTotal, market, currency);

  return (
    <ul className={styles.summaryReturnCosts}>
      <li className={styles.resumeItem}>
        <span>{t('pedido', 'pedido.devoluciones.subtotal', 'Subtotal')}</span>
        <span>{subtotalFormated}</span>
      </li>
      {returnCost !== null && (
        <li className={styles.resumeItem}>
          <span>{returnCost.label}</span>
          <span>{`${returnCost.price} ${returnCost.price !== 0 ? currency : ''}`}</span>
        </li>
      )}
      {taxes && (
        <li className={styles.resumeItem}>
          <span>{`${t('bolsa', 'impuestos', 'Impuestos')} [${taxes.taxDescription || '0.000%'}]`}</span>
          <span>{taxes.taxTotal ? roundAndFormatPrice(taxes.taxTotal, market, currency) : roundAndFormatPrice(0, market, currency)}</span>
        </li>
      )}
      <li className={styles.resumeTotal}>
        <span>{t('pedido', 'pedido.devoluciones.total', 'Total Return')}</span>
        <span className={styles.resumeTotalPrice}>{totalFormatted}</span>
      </li>
    </ul>
  );
};

SummaryReturnCosts.propTypes = {
  subtotal: number, // lo ideal seria que fuese un string que ya viniese formateada de back y no tener que calcular..
  returnCost: any,
  currency: string,
  taxes: any,
};

export const Items = ({ items = [], locale = defaultLocale, onSelect = null, selected = [], sizeEquivalences }) => {
  const market = getMarket(locale);
  const country = getCountryFromMarket(market);

  return (
    <div>
      {items.map((item, index) => {
        const parsedTarget =
          IS_NNORMAL && nnormalSizeMapCountries.includes(market) ? market
          : ['M', 'W'].includes(item.targetId) ? item.targetId
          : 'M';
        const equivalence = sizeEquivalences?.[item.targetId]?.[item.size]?.[country];
        const sizeLabel = getDomainValue({
          camper: equivalence ? `${equivalence} / EU ${item.targetId || item.target} ${item.size}` : item.size,
          camperlab: 'camper',
          nnormal: nnormalSizesMap[parsedTarget][item.size] ?? item.size,
        });
        return (
          <Row
            className={
              onSelect && (item.active === false || item?.prices?.current === 0) ?
                `${styles.rowItem} ${styles.rowItemDisabled}`
              : styles.rowItem + ` ${item.parentId ? 'ml-14' : ''}`
            }
            key={`${item.id}-${index}`}
          >
            {!item.parentId && onSelect && item.active && item?.prices?.current !== 0 && (
              <Checkbox className={styles.checkbox} checked={selected.includes(index)} onChange={(e) => onSelect(e.target.checked, index)} />
            )}
            <Col flex="120px">
              <img
                className={styles.itemThumbnail}
                src={item.images.sources && item.images.sources.filter((img) => img.type === 'md')[0].main}
                width={120}
                height={120}
                alt=""
              />
            </Col>
            <Col flex="auto" className={styles.colItemDetails}>
              <p className={styles.itemName}>
                {item.name}
                <br />
                <span className={styles.itemCode}>{item.productLabel || item.id}</span>
              </p>

              {item.parentId ?
                null
              : item.size ?
                <p
                  className={styles.sizes}
                >{`${country} ${TARGET_SIZE_LABEL_MARKETS.includes(market) && item.familyId && Number(item.familyId) === FAMILIES.SHOES ? item.targetId : ''} ${sizeLabel}`}</p>
              : <p className={styles.sizes}>&zwnj;</p>}

              {!item.parentId ?
                <p className={styles.price}>
                  {item.prices.previousFormated && item.prices.previousFormated.length && item.prices.previousFormated !== item.prices.currentFormated && (
                    <strike>{item.prices.previousFormated}</strike>
                  )}
                  {item.prices.currentFormated}
                </p>
              : null}
            </Col>
          </Row>
        );
      })}
    </div>
  );
};

Items.propTypes = {
  locale: string,
  items: array,
  onSelect: func,
  selected: array,
};

const STEPS = {
  SELECT_ARTICLE_AND_REASON: 1, // https://www.nachojacobo.com/camper/account-return-items/
  SELECT_RETURN_METHOD: 2, // https://www.nachojacobo.com/camper/account-return-items-1/
  SUMMARY_AND_SUBMIT_RETURN: 3, // https://www.nachojacobo.com/camper/account-return-items-2/
  RETURN_REGISTERED: 4, // https://www.nachojacobo.com/camper/account-return-items-3/
};

const REASONS = {
  SIZE: 'R00',
  QUALITY: 'R01',
};

export const DropoffPointButton = ({ market = 'ES', label, className = '', type = 'default', onClick = () => true, droppointUrl }) => {
  const { t } = useContext(i18nContext);
  const usesCorreosLabel = ['ES', 'US'];
  let labelToUse = null;

  if (usesCorreosLabel.includes(market)) {
    labelToUse = label || `${t('mis.pedidos', 'consulta.oficina.correos')} ${t('mis.pedidos', 'consulta.oficina.correos.aqui')}`;
  } else {
    labelToUse = `${t('mis.pedidos', 'consulta.oficina.dhlparcelshopuk')} ${t('mis.pedidos', 'consulta.oficina.dhlparcelshopuk.aqui')}`;
  }

  return (
    <Button className={className} type={type} onClick={onClick}>
      <a href={droppointUrl} target="_blank" rel="noreferrer">
        {labelToUse}
      </a>
    </Button>
  );
};

DropoffPointButton.propTypes = {
  market: string,
  className: string,
  label: string,
  type: string,
  onClick: func,
  droppointUrl: string,
};

export const returnMethodMap = {
  E1: 'home',
  E2: 'camper store',
  E3: 'sent by client',
  E4: 'post office',
};

const mergeArraysAtGivenIndex = (a, b, i = 0) => a.splice(i, 0, ...b);

export const ReturnItems = ({ order = {}, onClickBack = () => {}, changeAction = () => false, sizeEquivalences }) => {
  const { t, locale, profileData } = useI18n();
  const { userData } = useContext(userContext);
  const activeItems = order.products.filter((product) => product.active === true);
  const initialSelectedItems = [];
  if (activeItems.length === 1) {
    const isActiveItem = (element) => element.active === true;
    initialSelectedItems.push(order.products.findIndex(isActiveItem));
  }

  const [step, setStep] = useState(STEPS.SELECT_ARTICLE_AND_REASON);
  const [selectedItems, setSelectedItems] = useState(initialSelectedItems);
  const [rejectedItems, setRejectedItems] = useState([]);
  const [selectedReason, setSelectedReason] = useState(null);
  const [selectedSubreasons, setSelectedSubreasons] = useState([]);
  const [selectedSubreason, setSelectedSubreason] = useState(null);
  const [rejectRs, setRejectRs] = useState(null);
  const [rejectPickups, setRejectPickups] = useState([]);
  const [selectedMethod, setSelectedMethod] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [returnInfo, setReturnInfo] = useState(null);
  const [returnedOrder, setReturnedOrder] = useState(null);
  const [showingPickUpInStore, setShowingPickupInStore] = useState(false);
  const [addressForPickUp, setAddressForPickUp] = useState(null);
  const [urlForDroppoint, setUrlForDroppoint] = useState(null);
  const [changeSizeModalVisible, setChangeSizeModalVisible] = useState(false);
  const [wantsToContinueWithReturn, setWantsToContinueWithReturn] = useState(false);

  const market = getMarket(locale);
  const useRoyalMail = IS_NNORMAL && market === 'GB';
  const { config } = profileData;

  const handleSelectStore = () => true;

  const selectedRef = useRef(null);

  let eventDetail2 = `${order.reference}`;

  if (selectedItems.length > 0) {
    selectedItems.forEach((item) => {
      const product = JSON.parse(JSON.stringify(order.products[item]));
      eventDetail2 = `${eventDetail2}.${product.id}_${product.size}`;
    });
  }

  useEffect(() => {
    if (selectedRef.current === null && selectedItems.length > 0) {
      selectedItems.forEach((item) => {
        const productInfo = order?.products?.[item];

        dataLayerHandleEvent({
          event: 'select_return_item',
          event_category: 'return item',
          event_detail_1: '',
          event_detail_2: `${order.reference}.${productInfo.id}_${productInfo.size}`,
        });
      });
    }

    selectedRef.current = selectedItems;
  }, [selectedItems]);

  const handleOnSelect = (checked, index) => {
    let newSelectedItems = [...selectedItems];
    const productsCopy = JSON.parse(JSON.stringify(order.products));
    const itemToCheck = productsCopy[index];
    const additionalProductIndexes = [];
    if (Array.isArray(itemToCheck.additionalProducts) && itemToCheck.additionalProducts.length > 0) {
      productsCopy.forEach((product, i) => {
        if (itemToCheck.additionalProducts.includes(product.id)) {
          additionalProductIndexes.push(i);
        }
      });
    }

    if (checked) {
      dataLayerHandleEvent({
        event: 'select_return_item',
        event_category: 'return item',
        event_detail_1: '',
        event_detail_2: `${order.reference}.${itemToCheck.id}_${itemToCheck.size}`,
      });

      newSelectedItems.push(index);
      if (additionalProductIndexes.length > 0) {
        newSelectedItems = newSelectedItems.concat(additionalProductIndexes);
      }
      newSelectedItems = newSelectedItems.sort((a, b) => a - b);
      setSelectedItems(newSelectedItems);
    } else {
      newSelectedItems = newSelectedItems.filter((numberItem) => {
        if (additionalProductIndexes.includes(numberItem)) {
          return false;
        }
        return numberItem !== index;
      });
      setSelectedItems(newSelectedItems);
    }
  };

  const handleChangeReason = (value) => {
    const subreasons = order.rejectReasons.filter((reason) => reason.id === value)[0].subReasons;

    setSelectedReason(value);
    setSelectedSubreasons(subreasons);
    setSelectedSubreason(null);
  };

  const handleChangeSubreason = (value) => {
    setSelectedSubreason(value);
    setError('');
  };

  const launchSelectEvents = () => {
    dataLayerHandleEvent({
      event: 'select_return_reason',
      event_category: 'return item',
      event_detail_1: selectedReason,
      event_detail_2: eventDetail2,
    });

    dataLayerHandleEvent({
      event: 'select_return_subreason',
      event_category: 'return item',
      event_detail_1: `${selectedReason}-${selectedSubreason}`,
      event_detail_2: eventDetail2,
    });
  };

  const handleGoStep2 = async () => {
    const canGoToStep2 = selectedItems.length > 0 && selectedReason !== null && (selectedSubreason !== null || selectedSubreasons.length === 0);

    if (!canGoToStep2) {
      let errorText = '';

      if (selectedItems.length === 0) {
        errorText = t('pedido', 'pedido.devoluciones.error.elige.articulo', 'Please, select an article.');
      } else if (selectedReason === null) {
        errorText = t('pedido', 'pedido.devoluciones.error.elige.motivo', 'Please, select a reason.');
      } else if (selectedSubreasons.length > 0 && selectedSubreason === null) {
        errorText = t('pedido', 'pedido.devoluciones.error.elige.motivo', 'Please, select a reason.');
      }

      setError(errorText);
    } else if (canGoToStep2 && market === 'IT' && selectedReason === REASONS.SIZE && !wantsToContinueWithReturn) {
      // antes iba por config gratis_cambio_talla pero no quieren el popup
      // siempre así que de momento solo para Italia
      setChangeSizeModalVisible(true);
      return false;
    } else if (canGoToStep2 && selectedReason === REASONS.QUALITY) {
      launchSelectEvents();
      window.location.assign(
        `${window.location.origin}/${locale}/contacto?type=7&name=${order.applicant?.name}&surnames=${order.applicant?.surname}&email=${order.applicant?.email}&address=${order.applicant?.address}&city=${order.applicant?.city}&phone=${order.applicant?.telephone}&postalCode=${order.applicant?.postalCode}`,
      );
    } else {
      launchSelectEvents();
      setLoading(true);
      const rejectProducts = selectedItems.map((itemNumber) => {
        const product = JSON.parse(JSON.stringify(order.products[itemNumber]));
        const productObj = {
          id: product.id,
          position: product.position || String(itemNumber),
          size: product.size || '',
        };
        return productObj;
      });
      const { regionCode } = order.receiver;

      try {
        const rejectInfo = await rejectOrder1({
          lang: locale,
          email: order.applicant.email,
          reference: order.reference,
          reason: selectedReason,
          subReason: selectedSubreason,
          originalStore: order.store,
          originalExpedition: order.expedition,
          expeditionCenter: order.expeditionCenter,
          rejectProducts,
          region: regionCode,
        });

        const droppointRs = await getDroppointsInfo(null, locale);
        const newRejectedItems = selectedItems.map((itemNumber) => JSON.parse(JSON.stringify(order.products[itemNumber])));
        setRejectedItems(newRejectedItems);
        setRejectRs(rejectInfo);
        let pickups = JSON.parse(JSON.stringify(rejectInfo.pickup));
        if (droppointRs.droppoints && droppointRs.droppoints.length > 0) {
          let firstDroppointIndex = -1;
          let droppointBaseObject = {};
          let availableDroppoints = droppointRs.droppoints;
          const firstDroppoint = availableDroppoints.shift();
          pickups = pickups.map((pickup, index) => {
            if (pickup.type !== 'E4') {
              return pickup;
            }
            firstDroppointIndex = index;
            droppointBaseObject = { ...pickup };
            pickup.type = 'E4.0';
            pickup.typeText = firstDroppoint.title;
            pickup.description = firstDroppoint.description;
            pickup.droppointUrl = firstDroppoint.url;
            pickup.buttonText = firstDroppoint.buttonText;
            pickup.imgUrl = firstDroppoint.imgUrl;
            return pickup;
          });
          if (availableDroppoints.length > 0) {
            availableDroppoints = availableDroppoints.map((droppoint, index) => {
              const formattedDroppoint = {
                ...droppointBaseObject,
                type: `E4.${index + 1}`,
                typeText: droppoint.title,
                description: droppoint.description,
                droppointUrl: droppoint.url,
                buttonText: droppoint.buttonText,
                imgUrl: droppoint.imgUrl,
              };
              return formattedDroppoint;
            });
            mergeArraysAtGivenIndex(pickups, availableDroppoints, firstDroppointIndex + 1);
          }
        }
        setRejectPickups(pickups);
        setUrlForDroppoint(droppointRs.droppointUrl);
        setError(false);
        setLoading(false);
      } catch (rqError) {
        console.error(rqError);
        setError(t('pedido', 'pedido.devoluciones.error.ko', 'Sorry, we are unable to process your request at this time. Please, try later.'));
        setLoading(false);
      }
    }

    setWantsToContinueWithReturn(false);
    return true;
  };

  const handleChangeMethod = (e) => {
    dataLayerHandleEvent({
      event: 'select_return_method',
      event_category: 'return item',
      event_detail_1: e?.target?.value,
      event_detail_2: eventDetail2,
    });
    setSelectedMethod(e.target.value);
  };

  const handleGoStep3 = () => {
    const canGoToStep3 = selectedMethod !== null;
    if (!canGoToStep3) {
      setError(t('pedido', 'pedido.devoluciones.error.elige.metodo', 'Please, select a return method.'));
    } else {
      setError(null);

      if (window && window.document) {
        const [body] = document.getElementsByTagName('body');

        if (body) {
          body.scrollIntoView({ behavior: 'smooth' });
        }
      }

      setStep(STEPS.SUMMARY_AND_SUBMIT_RETURN);
    }
  };

  const handleSubmitReturn = async () => {
    setLoading(true);
    try {
      dataLayerHandleEvent({
        event: 'try_return_item',
        event_category: 'return item',
        event_detail_1: `${selectedReason}-${selectedSubreason}`,
        event_detail_2: eventDetail2,
      });
      const rejectProducts = selectedItems.map((itemNumber) => {
        const product = JSON.parse(JSON.stringify(order.products[itemNumber]));
        const productObj = {
          id: product.id,
          position: product.position || String(itemNumber),
          size: product.size || '',
        };
        return productObj;
      });
      const rejectParams = {
        lang: locale,
        order,
        reason: selectedReason,
        subReason: selectedSubreason,
        rejectProducts,
        useNewAddress: false,
        shippingMethodId: selectedMethod,
        applicant: order.applicant,
        expeditionCenterType: order.expeditionCenterType,
      };

      if (selectedMethod === 'E3') {
        rejectParams.returnDate = '';
      } else if (selectedMethod === 'E1') {
        rejectParams.returnDate = addressForPickUp.returnDate;
        // returnDate en base a form de method != E3
        rejectParams.applicant = {
          ...addressForPickUp,
          region: addressForPickUp.regionCode,
          email: order.applicant.email,
          edited: undefined,
        };

        if (addressForPickUp.edited) {
          rejectParams.useNewAddress = true;
        }
      }
      const result = await rejectOrder2(rejectParams);

      if (result.status && result.status === 'OK') {
        dataLayerHandleEvent({
          event: 'return_item',
          event_category: 'return item',
          event_detail_1: `${selectedReason}-${selectedSubreason}`,
          event_detail_2: eventDetail2,
        });
        const partiallyRejectedProducts = selectedItems.length !== order.products.filter((product) => product.active === true).length;
        const refundGA4 = {
          event: 'refund',
          ecommerce: {
            transaction_id: result.returnId,
          },
        };
        if (partiallyRejectedProducts) {
          const rejectedProducts = selectedItems.map((itemNumber) => {
            const product = JSON.parse(JSON.stringify(order.products[itemNumber]));
            const productObj = {
              ...product,
              id: product.id,
              position: product.position || String(itemNumber),
              size: product.size || '',
              price: product.prices,
            };
            return productObj;
          });
          refundGA4.ecommerce.items = parseBagItemsEcommerceGA4(rejectedProducts);
        }
        dataLayerHandleEvent(refundGA4);
        delete result.audit;
        setReturnInfo(result);
      } else {
        const errorText =
          result.errors && result.errors.length ?
            `${result.errors[0].code}: ${result.errors[0].message}`
          : t('pedido', 'pedido.devoluciones.error.ko', 'Sorry, we are unable to process your request at this time. Please, try later.');
        setError(errorText);
        setLoading(false);
      }
    } catch (rqError) {
      setError(rqError);
      setLoading(false);
    }
  };

  useEffect(() => {
    if (rejectRs !== null) {
      setStep(STEPS.SELECT_RETURN_METHOD);
    }
  }, [rejectRs]);

  useEffect(() => {
    const handleReturn = async () => {
      if (wantsToContinueWithReturn === true) {
        await handleGoStep2();
      }
    };

    handleReturn();
  }, [wantsToContinueWithReturn]);

  useEffect(() => {
    const getOrderInfoHandler = async () => {
      if (returnInfo !== null) {
        const newReturnedInfo = await getOrderInfo({
          lang: locale,
          email: order.applicant.email,
          locator: returnInfo.returnId,
        });

        setReturnedOrder(newReturnedInfo);
        setStep(STEPS.RETURN_REGISTERED);
        setError(false);
        setLoading(false);
      }
    };

    getOrderInfoHandler();
  }, [returnInfo]);

  const handleGoToPreviousStep = () => {
    const newStep = step - 1;

    if (newStep === 0 || step === STEPS.RETURN_REGISTERED) {
      onClickBack();
    } else {
      setStep(newStep);
    }
  };

  let returnCost = null;
  let urlDroppoint = urlForDroppoint;
  let buttonDropppointLabel = null;
  if (rejectPickups.length > 0) {
    const [selectedPickup] = rejectPickups.filter((item) => item.type === selectedMethod);

    if (selectedPickup) {
      const { type, total, buttonText } = selectedPickup;
      returnCost = {
        price: Number.isInteger(total) ? total * -1 : 0,
      };

      if (type === 'E1') {
        returnCost.label = t('compra.formulario', 'pick.up.home');
      } else if (type === 'E3') {
        returnCost.label = t('compra.formulario', 'envio.por.parte.del.cliente');
      } else if (type === 'E4') {
        if (market === 'DE') {
          returnCost.label = t('compra.formulario', 'envio.dhl.de');
          if (buttonText) {
            buttonDropppointLabel = buttonText;
          }
        } else {
          returnCost.label = t('compra.formulario', `envio.dropoff.${market.toLowerCase()}`);
        }
      } else if (type.includes('E4.')) {
        urlDroppoint = selectedPickup.droppointUrl;
        if (buttonText) {
          buttonDropppointLabel = buttonText;
        }
        if (market === 'DE') {
          returnCost.label = t('compra.formulario', 'envio.dhl.de');
        } else {
          returnCost.label = t('compra.formulario', `envio.dropoff.${market.toLowerCase()}`);
        }
      } else {
        returnCost.label = t('mis.pedidos', `gastos.envio.${order.expedition}.es`);
      }
    }
  }
  const taxes = {};
  if (rejectRs && rejectRs.products && rejectRs.products.length > 0) {
    taxes.taxTotal = rejectRs.products[0].taxTotal;
    taxes.taxDescription = rejectRs.products[0].taxDescription;
    taxes.taxTotalPrice = rejectRs.products.reduce((accumulator, current) => accumulator + current.totalPrice, 0);
  }

  return (
    <main>
      <div className={styles.content}>
        <div className={styles.contentMain}>
          <p className={styles.title}>
            {t('pedido', 'pedido.titulo', 'Order')} {order.reference}
          </p>
          {step !== STEPS.RETURN_REGISTERED && (
            <p onClick={handleGoToPreviousStep} className={styles.subtitle}>
              <ChevronLeftSVG />
              {t('pedido', 'pedido.devolver.subtitulo', 'Return Items')}
            </p>
          )}
          {step === STEPS.SELECT_ARTICLE_AND_REASON && (
            <>
              <p className={styles.step}>
                <strong>{t('pedido', 'pedido.devolucion.paso.1', 'Step 1.')}</strong>
                {t('pedido', 'pedido.devolucion.paso.1.titulo', 'Select Articles')}
              </p>
              <p className={styles.selectDescription}>{t('pedido', 'pedido.devolucion.selecciona.articulos', 'Select the articles you want to return:')}</p>
              <Items items={order.products} locale={locale} onSelect={handleOnSelect} selected={selectedItems} sizeEquivalences={sizeEquivalences} />
              <p className={styles.step}>
                <strong>{t('pedido', 'pedido.devolucion.paso.2', 'Step 2.')}</strong>
                {t('pedido', 'pedido.devolucion.paso.2.titulo', 'Reason of Return')}
              </p>
              <p className={styles.selectDescription}>{t('pedido', 'pedido.devolucion.selecciona.motivo', 'Tell us the reason of your return:')}</p>
              <div className={styles.reasonsDiv}>
                <Select
                  size="large"
                  className={styles.selectInput}
                  suffixIcon={<ChevronSVG />}
                  placeholder={t('pedido', 'pedido.devoluciones.selector.placeholder', 'Select an option')}
                  onChange={handleChangeReason}
                  value={selectedReason}
                >
                  {order.rejectReasons.map((reason, i) => (
                    <Option key={`${reason.id}${i}`} value={reason.id}>
                      {reason.description}
                    </Option>
                  ))}
                </Select>
                {selectedSubreasons.length > 0 && (
                  <Select
                    size="large"
                    className={styles.selectInput}
                    suffixIcon={<ChevronSVG />}
                    placeholder={t('pedido', 'pedido.devoluciones.selector.placeholder', 'Select an option')}
                    onChange={handleChangeSubreason}
                    value={selectedSubreason}
                  >
                    {selectedSubreasons.map((reason, i) => (
                      <Option key={`${reason.id}${i}`} value={reason.id}>
                        {t('mis.pedidos', reason.label, reason.name)}
                      </Option>
                    ))}
                  </Select>
                )}
              </div>
              <div className={styles.nextWrapper}>
                {error && <p className="error">{error}</p>}
                <Button loading={loading} onClick={handleGoStep2} type="primary" className={styles.nextStepBtn}>
                  {t('pedido', 'pedido.devoluciones.siguiente.paso', 'Next')}
                </Button>
              </div>
            </>
          )}
          {step === STEPS.SELECT_RETURN_METHOD && (
            <>
              <p className={styles.selectDescription}>{t('pedido', 'pedido.devolucion.comprueba.informacion', 'Check that the information is correct:')}</p>
              <Items items={rejectedItems} locale={locale} sizeEquivalences={sizeEquivalences} />
              <p className={styles.step}>
                <strong>{t('pedido', 'pedido.devolucion.paso.3', 'Step 3.')}</strong>
                {t('pedido', 'pedido.devolucion.paso.3.titulo', 'Select Return Method')}
              </p>

              <Radio.Group onChange={handleChangeMethod} value={selectedMethod} className={styles.radioGroup}>
                <Space direction="vertical" size="middle">
                  {rejectPickups
                    .filter((method) => method.active === true)
                    .map((method) => (
                      <Radio key={method.type} value={method.type}>
                        <span className="inline-flex items-center">
                          {method.typeText} {method.total > 0 ? `(${roundAndFormatPrice(method.total.toFixed(2), market, method.currency)})` : ''}
                          {method.imgUrl ?
                            <img className="ml-1 h-8" src={method.imgUrl}></img>
                          : null}
                        </span>
                        <br />
                        <small>{method.description}</small>
                      </Radio>
                    ))}
                </Space>
              </Radio.Group>
              {selectedMethod !== 'E1' && (
                <>
                  <div className={styles.otherReturnOptions}>
                    {t(
                      'pedido',
                      'other.return.options',
                      '*For other forms of return, please contact the customer service, the contact information is available on this page.',
                    )}
                  </div>
                  <div className={styles.separator} />
                </>
              )}
              {selectedMethod === 'E1' && (
                <>
                  {market === 'GR' && (
                    <p className={styles.policyMessage}>
                      {t('pedido', 'escribe.solo.alfabeto.latin', 'Please write the desired pick-up address in Latin alphabet')}
                    </p>
                  )}
                  <HomePickUp
                    order={order}
                    changeAddress={(values) => {
                      setAddressForPickUp(values);
                    }}
                    onChangeDate={() => {
                      dataLayerHandleEvent({
                        event: 'collection_date',
                        event_category: 'return item',
                        event_detail_1: '',
                        event_detail_2: eventDetail2,
                      });
                    }}
                    handleNext={handleGoStep3}
                    startEmpty={order.expedition === CORREOS || market === 'GR'}
                    onlyLatinAlphabet={market === 'GR'}
                  />
                </>
              )}
              <div className={styles.extraBtnWrapper}>
                {selectedMethod?.includes('E4') && urlForDroppoint !== null && (
                  <DropoffPointButton
                    market={market}
                    label={buttonDropppointLabel || null}
                    className={styles.extraBtn}
                    droppointUrl={urlDroppoint}
                    onClick={() => {
                      dataLayerHandleEvent({
                        event: 'find_nearest_post_office',
                        event_category: 'return item',
                        event_detail_1: '',
                        event_detail_2: eventDetail2,
                      });
                    }}
                  />
                )}
                {selectedMethod === 'E2' && rejectRs.stores && rejectRs.cities && (
                  <>
                    <Button
                      className={styles.extraBtn}
                      onClick={() => {
                        dataLayerHandleEvent({
                          event: 'find_nearest_store',
                          event_category: 'return item',
                          event_detail_1: '',
                          event_detail_2: eventDetail2,
                        });
                        setShowingPickupInStore(true);
                      }}
                    >
                      {t('mis.pedidos', 'find.nearest.store')}
                    </Button>
                  </>
                )}
              </div>
              <div className={styles.nextWrapper}>
                {error && <p className="error">{error}</p>}
                {selectedMethod !== 'E1' && // home pick up tiene su propio botón
                  selectedMethod !== 'E2' && ( // return en stores es solo informativo
                    <Button loading={loading} onClick={handleGoStep3} type="primary" className={styles.nextStepBtn}>
                      {t('pedido', 'pedido.devoluciones.siguiente.paso', 'Next')}
                    </Button>
                  )}
              </div>
            </>
          )}
          {step === STEPS.SUMMARY_AND_SUBMIT_RETURN && (
            <div className={styles.summaryStep}>
              <p className={styles.refundTitle}>
                <strong>{t('pedido', 'pedido.devolucion.paso.4.resumen.titulo', 'Refund Summary')}</strong>
              </p>
              <p>
                {t(
                  'pedido',
                  'pedido.devolucion.paso.4.resumen.descripcion',
                  'We will refund the total value of your returned item less the cost of the return service if applicable:',
                )}
              </p>
              <SummaryReturnCosts
                subtotal={rejectedItems.reduce((accumulator, current) => {
                  if (!current.parentId) {
                    return accumulator + current.prices.current;
                  }
                  return accumulator;
                }, 0)}
                returnCost={returnCost}
                currency={rejectedItems && rejectedItems.length > 0 ? rejectedItems[0].prices?.currency || '' : ''}
                taxes={COUNTRIES_WITH_TAXES.includes(market) && Object.keys(taxes).length > 0 ? taxes : null}
              />
              {/* El 'Step 4. Select Refund Option' de la maqueta no se hace por ahora */}
              <p>
                {t(
                  'pedido',
                  'pedido.devolucion.paso.4.resumen.confirmacion',
                  'Please ensure all the information above is correct before proceeding with your return.',
                )}
              </p>
              <div className={styles.nextWrapper}>
                {error && <p className="error">{error}</p>}
                <Button loading={loading} onClick={handleSubmitReturn} type="primary" className={styles.nextStepBtn}>
                  {t('pedido', 'pedido.devoluciones.boton.devolver', 'Return Item')}
                </Button>
              </div>
            </div>
          )}
          {step === STEPS.RETURN_REGISTERED && (
            <div className={styles.registeredStep}>
              <p className={styles.returnRegisteredTitle}>
                <strong>{t('pedido', 'pedido.devolucion.registrada.titulo', 'Return Registered')}</strong>
              </p>
              <p>
                {t('pedido', `pedido.devolucion.registrada.descripcion.pedido.n${useRoyalMail ? '.gb' : ''}`, 'Your return request nº')}{' '}
                <strong>{returnInfo.returnId} </strong>
                {t('pedido', `pedido.devolucion.registrada.descripcion.pedido.registrado${useRoyalMail ? '.gb' : ''}`, 'has been successfully registered.')}
              </p>
              <p
                dangerouslySetInnerHTML={{
                  __html: t(
                    'pedido',
                    `pedido.devolucion.registrada.descripcion.enviado.email${useRoyalMail ? '.gb' : ''}`,
                    'We have just sent you an email with the form and the instructions to follow in order to process your return.',
                  ),
                }}
              />
              <p
                className={styles.returnRegisteredDetails}
                dangerouslySetInnerHTML={{
                  __html: t('pedido', `pedido.devolucion.registrada.descripcion.enviado.contacto${useRoyalMail ? '.gb' : ''}`).replace(
                    /<TEL>/g,
                    `<a href="tel:${profileData.phone}"><u> ${profileData.phone}</u></a>`,
                  ),
                }}
              ></p>

              {market === 'US' ?
                <p>{t('pedido', 'pedido.devolucion.fedex', '***Carried by Fedex.')}</p>
              : null}

              {returnInfo.pdfUrl && (
                <a
                  className={`${styles.returnLink} ${styles.printLabel}`}
                  href={useRoyalMail ? ROYAL_MAIL_URL : returnInfo.pdfUrl}
                  target="_blank"
                  rel="noreferrer"
                >
                  {t('mis.pedidos', `imprimir.formulario.pdf${useRoyalMail ? '.gb' : ''}`)}
                </a>
              )}

              <LabelButtons data={returnedOrder} />

              {getDomainValue({
                camper: (
                  <a className={styles.returnLink} href={`/${locale}/myaccount/myorders${userData !== null ? '' : '/orderdetails'}`}>
                    {userData !== null ?
                      t('pedido', 'pedido.devolucion.registrada.boton.mis.pedidos', 'Go to My Orders')
                    : t('pedido', 'pedido.devolucion.registrada.boton.pedido', '***Go back to the order')}
                  </a>
                ),
                nnormal: (
                  <a className={styles.returnLink} href={`/${locale}/myaccount/myorders${userData !== null ? '' : '/orderdetails'}`}>
                    {userData !== null ?
                      t('pedido', 'pedido.devolucion.registrada.boton.mis.pedidos', 'Go to My Orders')
                    : t('pedido', 'pedido.devolucion.registrada.boton.pedido', '***Go back to the order')}
                  </a>
                ),
                camperlab: null,
              })}

              <p className={styles.orderSummary}>{t('mi.cuenta', 'resumen.pedido', 'Your order summary')}</p>
              <Items items={rejectedItems} locale={locale} sizeEquivalences={sizeEquivalences} />
              {/*
              <PurchaseSection
                market={market}
                subtotal={returnedOrder.subtotal}
                subtotalFormated={roundAndFormatPrice(returnedOrder.subtotal, market, returnedOrder.currentShipment.shipmentsCostsPrice.currency)}
                totalFormated={roundAndFormatPrice(returnedOrder.total, market, returnedOrder.currentShipment.shipmentsCostsPrice.currency)}
                selectedShipment={returnedOrder.currentShipment}
                onlySummary
                screen='order'
                codCost={returnedOrder.shipmentCod > 0 ? roundAndFormatPrice(returnedOrder.shipmentCod, market, returnedOrder.currentShipment.shipmentsCostsPrice.currency) : null}
              /> */}
              <SummaryReturnCosts
                returnCost={returnCost}
                currency={rejectedItems && rejectedItems.length > 0 ? rejectedItems[0].prices?.currency || '' : ''}
                taxes={COUNTRIES_WITH_TAXES.includes(market) && Object.keys(taxes).length > 0 ? taxes : null}
                subtotal={rejectedItems.reduce((accumulator, current) => {
                  if (!current.parentId) {
                    return accumulator + current.prices.current;
                  }
                  return accumulator;
                }, 0)}
              />
            </div>
          )}
        </div>

        <div className={styles.contentSide}>
          <ContactBlock />
        </div>
      </div>
      {rejectRs && rejectRs.stores && rejectRs.cities && (
        <PickUpInStoreModal
          shipment={{ stores: rejectRs.stores, cities: rejectRs.cities }}
          onClose={() => {
            setShowingPickupInStore(false);
          }}
          visible={showingPickUpInStore}
          handleSelectStore={handleSelectStore}
        />
      )}
      {market === 'IT' && (
        <Modal visible={changeSizeModalVisible} closable onCancel={() => setChangeSizeModalVisible(false)} footer={false}>
          <h2 className="pt-10 text-center text-2xl">{t('pedido', 'free.size.exchange')}</h2>
          <p className="py-2 text-center">{t('pedido', 'free.size.exchange.description')}</p>
          <div className="flex flex-col items-center">
            <Button className="mb-5" onClick={() => changeAction(ORDER_DETAILS_ACTIONS.CHANGE)} type="primary">
              {t('pedido', 'want.another.size')}
            </Button>
            <Button
              onClick={() => {
                setWantsToContinueWithReturn(true);
                setChangeSizeModalVisible(false);
              }}
              type="secondary"
            >
              {t('pedido', 'continue.with.return')}
            </Button>
          </div>
        </Modal>
      )}
    </main>
  );
};

ReturnItems.propTypes = {
  order: object,
  onClickBack: func,
  changeAction: func,
};

export default ReturnItems;
